import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EmailQueryDto, EmailQueryInterface } from '@zaoblako/shared/common/dtos/email-query.dto';
import { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import { ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';
import {
  ProfileRegisterDto,
  ProfileRegisterInterface
} from '@zaoblako/shared/modules/security/dtos/profile/profile-register.dto';
import { RoleFindInterface } from '@zaoblako/shared/modules/security/dtos/role/role-find.dto';
import { UserCreateDto, type UserCreateInterface } from '@zaoblako/shared/modules/security/dtos/user/user-create.dto';
import { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { WorkScheduleFindInterface } from '@zaoblako/shared/modules/security/dtos/work-schedule/work-schedule-find.dto';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { RoleService } from '@zaoblako/web/core/services/security/role.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { WorkScheduleService } from '@zaoblako/web/core/services/security/work-schedule.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ApprovalSchemeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/approval-scheme-single-select/approval-scheme-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputMaskModule } from 'primeng/inputmask';
import { StepsModule } from 'primeng/steps';
import { forkJoin, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-user-create-form',
  templateUrl: './user-create-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    ButtonModule,
    InputMaskModule,
    FormsModule,
    MultiSelectComponent,
    SingleSelectComponent,
    SpinnerComponent,
    ContragentAutoCompleteComponent,
    StepsModule,
    ApprovalSchemeSingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserCreateFormComponent extends BaseFormComponent<UserCreateInterface> implements OnInit {
  @Input() public readonly index: number;
  public email: EmailQueryInterface = <EmailQueryInterface>{};
  public errorEmail: ValidationErrorInterface<EmailQueryInterface>;

  public profile: ProfileFindInterface;
  public profileRegister: ProfileRegisterInterface = <ProfileRegisterInterface>{};
  public errorProfileRegister: ValidationErrorInterface<ProfileRegisterInterface>;

  public user = <UserCreateInterface>{ profile: {} };
  public dto = UserCreateDto;
  public engine: 'class-transformer' | 'serde' = 'serde';
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public steps: MenuItem[] = [{ label: 'Email' }, { label: 'ФИО' }, { label: 'Доп. информация' }];
  public activeIndexStep = 0;

  public roles: RoleFindInterface[] = [];
  public schedules: WorkScheduleFindInterface[] = [];

  public constructor(
    private readonly userService: UserService,
    private readonly profileService: ProfileService,
    private readonly formStoreService: FormStoreService,
    private readonly roleService: RoleService,
    private readonly workScheduleService: WorkScheduleService,
    private readonly router: Router,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.roleService.find<RoleFindInterface>(),
      this.workScheduleService.find<WorkScheduleFindInterface>()
    ]).subscribe(([roles, schedules]) => {
      this.roles = roles;
      this.schedules = schedules;
      this.cdRef.markForCheck();
    });
  }

  public saveUser() {
    const closeForm = (user: UserFindInterface, withProfile = false) => {
      this.formStoreService.setLoading(false, this.index);
      if (!withProfile) {
        this.errorService.addSuccessMessage('Пользователь создан');
      } else {
        this.errorService.addSuccessMessage(
          'Пользователь создан',
          'Данный пользователь был найден в реестре системы, все его данные были автоматически заполнены'
        );
      }
      if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
        this.formStoreService.closeSidebar(this.index);
        this.router.navigate(['/user/card/' + user.id]).catch(console.error);
      } else {
        this.formStoreService.saveNext(user, this.index);
      }
    };

    if (!this.activeIndexStep) {
      const errors = this.validationService.parseAndValidate(this.email, EmailQueryDto)?.[1];
      if (errors) {
        this.errorEmail = errors.validationErrors;
        return;
      }

      this.formStoreService.setLoading(true, this.index);

      this.profileService.testEmail(this.email).subscribe({
        next: (data) => {
          if (data.user) {
            this.errorService.addCustomError('Пользователь с таким email уже существует');
          } else if (data.profile) {
            this.profile = data.profile;
            this.activeIndexStep = 2;
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          } else {
            this.activeIndexStep = 1;
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          }
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else if (this.activeIndexStep === 1) {
      this.profileRegister.email = this.email.email;
      const errors = this.validationService.parseAndValidate(this.profileRegister, ProfileRegisterDto)?.[1];
      if (errors) {
        this.errorProfileRegister = errors.validationErrors;
        return;
      }

      this.formStoreService.setLoading(true, this.index);

      this.profileService.create<ProfileFindInterface>(this.profileRegister).subscribe({
        next: (profile) => {
          this.profile = profile;
          this.activeIndexStep = 2;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else if (this.activeIndexStep === 2) {
      this.user.profile = this.profile;
      if (this.checkValidate(this.user).valid) {
        this.formStoreService.setLoading(true, this.index);

        this.userService.create<UserFindInterface>(this.user).subscribe({
          next: (user) => {
            closeForm(user);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public back() {
    if (this.activeIndexStep === 2) {
      this.activeIndexStep = 0;
    } else {
      this.activeIndexStep--;
    }
  }
}
